'use client';

import * as React from 'react';
import { LogOut, User as UserIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { useAuthBootstrap, getDisplayUser, logout } from '@/features/auth';

/**
 * Header profile menu — renders nothing until a real SSO session exists
 * (SSO off, not signed in, or a public/lead surface that never bootstraps
 * all resolve to null, same as today's no-menu behavior). Callers opt in
 * explicitly via AppHeader's `showAccountMenu` — this must never render on
 * a lead-facing surface, which is why it isn't on by default.
 *
 * Log out is hidden below the `sm` breakpoint on purpose: small/mobile
 * viewports show identity only (name + phone), no sign-out action.
 */
export function UserMenu() {
  const { enabled, status } = useAuthBootstrap();
  const user = status === 'ready' ? getDisplayUser() : null;

  if (!enabled || !user || (!user.fullName && !user.phone)) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[var(--border)] bg-[var(--surface)] text-[var(--text-secondary)] transition-colors hover:border-[color-mix(in_srgb,var(--primary)_35%,transparent)] hover:text-[var(--primary)]"
        >
          <UserIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          {user.fullName && (
            <p className="text-sm font-semibold text-[var(--text-primary)]">{user.fullName}</p>
          )}
          {user.phone && (
            <p className="mt-0.5 text-xs font-normal text-[var(--text-muted)]">{user.phone}</p>
          )}
        </DropdownMenuLabel>

        {/* Small/mobile viewports stop here — identity only, no logout action. */}
        <div className="hidden sm:block">
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={() => {
              void import('@/features/auth').then(({ logout }) => logout());
            }}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Log out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
